/**
 * Credential-lookup error-state map (SIM-2 / ACS-4126).
 *
 * Maps a Backend Auth `authenticationMethods` (OI-4) error code — from the
 * `{ errors: [{ errorCode, errorMessage }] }` envelope — to how the
 * CredentialEntryScreen should present it. Source of truth: the error table in
 * artifacts/03-architecture/target-state/api-contracts/auth-service-apis.md §1
 * and the Figma "5. Errors" board (node 1307-109896).
 *
 * Scope: credential-lookup step ONLY. OTP-step error codes are SIM-4.
 *
 * `kind` values:
 *  - 'generic'              — inline generic invalid-format message (AC-018).
 *  - 'notFound'             — inline not-found message (AC-019) + a Create
 *                             Account link to /register. BR-020: identical for
 *                             phone and email so we never leak which was read.
 *  - 'backendMessage'       — inline; render the backend-provided errorMessage
 *                             verbatim (AC-021 closed/blocked/sys-reset — the
 *                             message differentiates; the code does not).
 *  - 'modalStoreRegistered' — ERR_400004: store-registered user with incomplete
 *                             registration. Handled specially by the screen —
 *                             open the "Check Your Email" modal ONLY when the
 *                             entered credential was an email; otherwise fall
 *                             back to the generic inline error.
 *
 * The locked-account-with-"Reset your Password"-link Figma variant is
 * intentionally OUT of scope for SIM-2 (ERR_ACCOUNT_CLOSED renders as plain
 * backend message, no link).
 */
const CREDENTIAL_LOOKUP_ERROR_MAP = {
    ERR_PHONE_INVALID_FORMAT: { kind: 'generic' },
    ERR_PHONE_NOT_FOUND: { kind: 'notFound' },
    ERR_EMAIL_NOT_FOUND: { kind: 'notFound' },
    ERR_ACCOUNT_CLOSED: { kind: 'backendMessage' },
    ERR_ACCOUNT_SYS_LOGIN: { kind: 'backendMessage' },
    ERR_400004: { kind: 'modalStoreRegistered' }
};

// Any unrecognized code (or a code-less 5xx) falls back to the generic inline
// error so the user is never stuck without feedback.
const DEFAULT_LOOKUP_ERROR = { kind: 'generic' };

/**
 * @param {string} [errorCode]
 * @returns {{ kind: 'generic'|'notFound'|'backendMessage'|'modalStoreRegistered' }}
 */
export function getCredentialLookupError(errorCode) {
    return CREDENTIAL_LOOKUP_ERROR_MAP[errorCode] || DEFAULT_LOOKUP_ERROR;
}

export {
    CREDENTIAL_LOOKUP_ERROR_MAP, DEFAULT_LOOKUP_ERROR
};

export default getCredentialLookupError;
